<template>
  <v-dialog
    v-model="dialogVisible"
    :persistent="loading"
    :width="width"
    :max-width="maxWidth"
    @input="handleDialogChange"
  >
    <v-card v-if="dialogVisible" :rounded="cardRounded" :class="cardClasses">
      <!-- Header with icon and title -->
      <div class="d-flex justify-space-between align-center mx-2 mb-5">
        <v-icon :color="iconColor" :size="iconSize" :class="iconClasses">
          {{ icon }}
        </v-icon>
        <v-card-title :class="titleClasses" v-html="title"> </v-card-title>
      </div>

      <!-- Error message -->
      <div v-if="error" class="text-left word-break mb-4">
        <div v-html="errorMessage"></div>
        <div v-if="showContactLink" class="mt-2">
          Please try again or
          <a :href="resolvedContactHref" target="_blank">{{ contactText }}</a>
          if the problem continues.
        </div>
      </div>

      <!-- Confirmation message -->
      <div v-else class="text-left word-break">
        <p class="ml-1 mb-3" v-html="confirmationMessage"></p>

        <!-- DELETE confirmation input -->
        <div v-if="requireDeleteConfirmation">
          <p class="ml-1 mb-1">Confirm {{ itemType }} deletion by typing 'DELETE' below:</p>
          <v-text-field
            v-model="deleteConfirmation"
            placeholder="DELETE"
            outlined
            dense
            color="brandCyan"
            :disabled="loading"
            :rules="deleteConfirmationRules"
            :error-messages="deleteConfirmationErrors"
            @input="handleConfirmationInput"
          ></v-text-field>
        </div>
      </div>

      <!-- Action buttons -->
      <v-card-actions :class="actionsClasses">
        <v-btn
          :color="cancelButtonColor"
          :class="cancelButtonClasses"
          @click="handleCancel"
          :outlined="cancelButtonOutlined"
          :width="buttonWidth"
          :disabled="loading"
        >
          {{ cancelButtonText }}
        </v-btn>

        <v-btn
          v-if="!error"
          :color="confirmButtonColor"
          :class="confirmButtonClasses"
          :disabled="!canConfirm"
          @click="handleConfirm"
          :width="buttonWidth"
          :loading="loading"
          :depressed="confirmButtonDepressed"
        >
          {{ confirmButtonText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import whitelabelMixin from "@/mixins/whitelabelMixin";

export default {
  name: "DeleteConfirmationDialog",
  // whitelabelMixin supplies supportEmail for the tenant-aware fallback in
  // resolvedContactHref below. This component previously hardcoded its own
  // default contact link; that default is now derived from whitelabel config
  // instead of a literal, while still allowing any caller to override it.
  mixins: [whitelabelMixin],
  props: {
    // Dialog state
    value: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },

    // Content
    title: {
      type: String,
      required: true,
    },
    confirmationMessage: {
      type: String,
      required: true,
    },
    itemType: {
      type: String,
      default: "item",
    },
    itemName: {
      type: String,
      default: "",
    },

    // Error handling
    error: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: "We ran into a problem while deleting the item",
    },
    showContactLink: {
      type: Boolean,
      default: true,
    },
    contactHref: {
      type: String,
      // null triggers the tenant-aware fallback in resolvedContactHref below.
      // Callers that pass an explicit href keep full control, same as before.
      default: null,
    },
    contactText: {
      type: String,
      default: "contact us",
    },

    // DELETE confirmation
    requireDeleteConfirmation: {
      type: Boolean,
      default: true,
    },

    // Styling
    width: {
      type: [String, Number],
      default: "500",
    },
    maxWidth: {
      type: [String, Number],
      default: "90vw",
    },
    cardRounded: {
      type: [String, Number],
      default: "0",
    },
    cardClasses: {
      type: [String, Array, Object],
      default: () => ["d-flex", "justify-center", "flex-column", "pa-6"],
    },

    // Icon
    icon: {
      type: String,
      default: "mdi-alert",
    },
    iconColor: {
      type: String,
      default: "error",
    },
    iconSize: {
      type: [String, Number],
      default: "x-large",
    },
    iconClasses: {
      type: [String, Array, Object],
      default: () => ["exit-warning-icon", "mr-4"],
    },

    // Title
    titleClasses: {
      type: [String, Array, Object],
      default: () => ["word-break", "align-text-left", "exit-warning-text"],
    },

    // Buttons
    cancelButtonText: {
      type: String,
      default: "Cancel",
    },
    cancelButtonColor: {
      type: String,
      default: "brandCyan",
    },
    cancelButtonClasses: {
      type: [String, Array, Object],
      default: () => ["mx-1"],
    },
    cancelButtonOutlined: {
      type: Boolean,
      default: true,
    },

    confirmButtonText: {
      type: String,
      default: "Delete",
    },
    confirmButtonColor: {
      type: String,
      default: "brandCyan",
    },
    confirmButtonClasses: {
      type: [String, Array, Object],
      default: () => ["white--text", "mx-1"],
    },
    confirmButtonDepressed: {
      type: Boolean,
      default: true,
    },

    buttonWidth: {
      type: [String, Number],
      default: "130",
    },

    // Actions container
    actionsClasses: {
      type: [String, Array, Object],
      default: () => ["pt-3", "d-flex", "justify-center", "align-center"],
    },
  },
  data() {
    return {
      deleteConfirmation: "",
      deleteConfirmationErrors: [],
    };
  },
  computed: {
    resolvedContactHref() {
      return (
        this.contactHref || `mailto:${this.supportEmail}?subject=Problems while deleting items`
      );
    },
    dialogVisible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    canConfirm() {
      if (this.loading) return false;
      if (this.error) return false;

      if (this.requireDeleteConfirmation) {
        return this.deleteConfirmation && this.deleteConfirmation.toUpperCase() === "DELETE";
      }

      return true;
    },
    deleteConfirmationRules() {
      if (!this.requireDeleteConfirmation) return [];

      return [(v) => !!(v && v.toUpperCase() === "DELETE") || "Enter DELETE to confirm"];
    },
  },
  watch: {
    dialogVisible(newVal) {
      if (!newVal) {
        this.resetForm();
      }
    },
    error(newVal) {
      if (newVal) {
        this.deleteConfirmationErrors = [];
      }
    },
  },
  methods: {
    handleDialogChange(value) {
      this.dialogVisible = value;
    },

    handleConfirmationInput(value) {
      this.deleteConfirmation = value;
      this.deleteConfirmationErrors = [];
    },

    handleCancel() {
      this.$emit("cancel");
      this.dialogVisible = false;
    },

    handleConfirm() {
      if (!this.canConfirm) return;

      this.$emit("confirm", {
        itemName: this.itemName,
        itemType: this.itemType,
        deleteConfirmation: this.deleteConfirmation,
      });
    },

    resetForm() {
      this.deleteConfirmation = "";
      this.deleteConfirmationErrors = [];
    },
  },
};
</script>

<style scoped>
.exit-warning-icon {
  flex-shrink: 0;
}

.exit-warning-text {
  flex: 1;
}

.word-break {
  word-break: break-word;
}

/* Custom button styling */
.v-btn {
  text-transform: none;
  font-weight: 500;
}

/* Error state styling */
.text-left a {
  color: inherit;
  text-decoration: underline;
}

.text-left a:hover {
  text-decoration: none;
}
</style>
